import { defaultBrowserStorage, type StoragePort } from "./ports.ts";

export const SFX_VOLUME_STORAGE_KEY = "dawn.sfxVolume";
export const DEFAULT_SFX_VOLUME = 5;
export const SFX_VOLUME_MIN = 0;
export const SFX_VOLUME_MAX = 10;

function resolveStorage(storage?: StoragePort | null): StoragePort | null {
  if (storage !== undefined) {
    return storage;
  }
  return defaultBrowserStorage();
}

export function clampSfxVolume(level: number): number {
  if (!Number.isFinite(level)) {
    return DEFAULT_SFX_VOLUME;
  }
  return Math.max(SFX_VOLUME_MIN, Math.min(SFX_VOLUME_MAX, Math.round(level)));
}

export function getStoredSfxVolume(storage?: StoragePort | null): number {
  const store = resolveStorage(storage);
  if (!store) {
    return DEFAULT_SFX_VOLUME;
  }

  const stored = store.getItem(SFX_VOLUME_STORAGE_KEY);
  if (stored === null) {
    setStoredSfxVolume(DEFAULT_SFX_VOLUME, store);
    return DEFAULT_SFX_VOLUME;
  }

  const parsed = Number.parseInt(stored, 10);
  if (!Number.isInteger(parsed) || parsed < SFX_VOLUME_MIN || parsed > SFX_VOLUME_MAX) {
    setStoredSfxVolume(DEFAULT_SFX_VOLUME, store);
    return DEFAULT_SFX_VOLUME;
  }

  return parsed;
}

export function setStoredSfxVolume(level: number, storage?: StoragePort | null): void {
  const store = resolveStorage(storage);
  if (!store) {
    return;
  }
  store.setItem(SFX_VOLUME_STORAGE_KEY, String(clampSfxVolume(level)));
}

export function sfxVolumeToGain(level: number): number {
  return clampSfxVolume(level) / SFX_VOLUME_MAX;
}

/** Fill `{level}` in a localized SFX volume label template. */
export function formatSfxVolumeLabel(template: string, level: number): string {
  return template.replace("{level}", String(clampSfxVolume(level)));
}

export interface SfxVolumePlayer {
  setMuted(muted: boolean): void;
  setVolume(gain: number): void;
}

export function applySfxVolume(player: SfxVolumePlayer, level: number): void {
  const clamped = clampSfxVolume(level);
  if (clamped === 0) {
    player.setMuted(true);
    return;
  }
  player.setMuted(false);
  player.setVolume(sfxVolumeToGain(clamped));
}
